/**
 * Shared dimension split types used by AP bills, AR invoices, and billing services.
 */

export interface DimensionSplit {
  splitId?: string;
  id: string;
  amount: number;
  percent: number;
  code?: string;
  name?: string;
}

/**
 * `segmentValueId` names a predefined `ApSegmentValue`. `textValue` is the free-text
 * alternative for segments with `hasPredefinedValues = false` + `dataType = STRING` —
 * exactly one of the two is set per split (enforced server-side as
 * `SEGMENT_VALUE_CONFLICT` when both are present, `SEGMENT_VALUE_REQUIRED` when
 * neither is). Both fields are optional here only because a split is one or the
 * other, never both required at once — callers must still branch on which is set.
 * AP bill classification is the only caller that populates `textValue` today; every
 * other consumer of this shared type (AR, procurement, billing, FP&A) keeps using
 * `segmentValueId` exactly as before.
 */
export interface SegmentSplit {
  splitId?: string;
  segmentId: string;
  segmentValueId?: string;
  textValue?: string;
  amount: number;
  percent: number;
  segmentName?: string;
  value?: string;
  label?: string | null;
}

export class SegmentValueConflictError extends Error {
  readonly code = 'SEGMENT_VALUE_CONFLICT' as const;

  constructor() {
    super('SEGMENT_VALUE_CONFLICT');
    this.name = 'SegmentValueConflictError';
  }
}

export type ResolvedSegmentValue =
  { kind: 'catalog'; value: string } | { kind: 'text'; value: string } | null;

export type SegmentSplitValueConflictMode = 'skip' | 'throw';

/**
 * Canonical parser for catalog IDs and raw text, including persisted server-produced rows.
 *
 * Persisted classifications are historical input and must be survivable by readers: a split
 * carrying both fields is malformed and is skipped by default. Write-side callers that can
 * translate the error into the stable HTTP validation response may explicitly request `throw`.
 */
export function resolveSegmentSplitValue(
  input: {
    segmentValueId?: unknown;
    textValue?: unknown;
  },
  options: { onConflict?: SegmentSplitValueConflictMode } = {},
): ResolvedSegmentValue {
  const segmentValueId =
    typeof input.segmentValueId === 'string' && input.segmentValueId.length > 0
      ? input.segmentValueId
      : null;
  const textValue =
    typeof input.textValue === 'string' && input.textValue.length > 0 ? input.textValue : null;
  if (segmentValueId && textValue) {
    if (options.onConflict === 'throw') throw new SegmentValueConflictError();
    return null;
  }
  if (textValue) return { kind: 'text', value: textValue };
  if (segmentValueId) return { kind: 'catalog', value: segmentValueId };
  return null;
}

/**
 * Display label for a custom-segment split.
 * Order: free text, enriched value/label, catalog label resolved from `segmentValueId`,
 * then a short technical id only when nothing else is available.
 */
export function resolveCustomSegmentDisplayLabel(input: {
  textValue?: string | null;
  value?: string | null;
  label?: string | null;
  resolvedCatalogLabel?: string | null;
  segmentValueId?: string | null;
}): string {
  const textValue = input.textValue?.trim();
  if (textValue) return textValue;
  const enriched = input.value?.trim() || input.label?.trim();
  if (enriched) return enriched;
  const catalog = input.resolvedCatalogLabel?.trim();
  if (catalog) return catalog;
  const segmentValueId = input.segmentValueId?.trim();
  return segmentValueId ? segmentValueId.slice(0, 8) : '—';
}

/** Stable React key for a split. Free-text splits have an empty `segmentValueId`. */
export function customSegmentSplitKey(split: {
  segmentValueId?: string | null;
  textValue?: string | null;
  splitId?: string | null;
}): string {
  const segmentValueId = split.segmentValueId?.trim();
  if (segmentValueId) return segmentValueId;
  const splitId = split.splitId?.trim();
  if (splitId) return splitId;
  return `text:${split.textValue ?? ''}`;
}

export const CLASSIFICATION_SEGMENT_FILTER_LIMITS = {
  maxTextLength: 200,
} as const;

const CLASSIFICATION_SEGMENT_FILTER_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type ClassificationSegmentFilter = {
  /** Match definitions with the same name, data type and catalog mode in each bill's entity. */
  matchEquivalent?: boolean;
} & (
  | { segmentId: string; kind: 'VALUE_ID'; values: string[] }
  | { segmentId: string; kind: 'TEXT'; value: string }
);

/**
 * Accepts a query-string JSON payload or an already-parsed array.
 * Returns undefined when the value is absent. Throws when the payload is present but invalid.
 */
export function parseClassificationSegmentFilters(
  value: unknown,
): ClassificationSegmentFilter[] | undefined {
  if (value == null || value === '') return undefined;
  let raw: unknown = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value) as unknown;
    } catch {
      throw new Error('classificationSegmentFilters must be a JSON array');
    }
  }
  if (!Array.isArray(raw)) {
    throw new Error('classificationSegmentFilters must be an array');
  }
  return raw.map((item) => parseOneClassificationSegmentFilter(item));
}

function parseOneClassificationSegmentFilter(item: unknown): ClassificationSegmentFilter {
  if (!item || typeof item !== 'object') {
    throw new Error('classificationSegmentFilters item must be an object');
  }
  const record = item as Record<string, unknown>;
  const segmentId = typeof record.segmentId === 'string' ? record.segmentId.trim() : '';
  if (!CLASSIFICATION_SEGMENT_FILTER_UUID.test(segmentId)) {
    throw new Error('classificationSegmentFilters.segmentId must be a UUID');
  }
  if (record.matchEquivalent !== undefined && typeof record.matchEquivalent !== 'boolean') {
    throw new Error('classificationSegmentFilters.matchEquivalent must be a boolean');
  }
  const scope = record.matchEquivalent === true ? { matchEquivalent: true } : {};
  if (record.kind === 'VALUE_ID') {
    if (!Array.isArray(record.values) || record.values.length === 0) {
      throw new Error('classificationSegmentFilters VALUE_ID requires values');
    }
    const values = record.values.map((entry) => {
      const id = typeof entry === 'string' ? entry.trim() : '';
      if (!CLASSIFICATION_SEGMENT_FILTER_UUID.test(id)) {
        throw new Error('classificationSegmentFilters VALUE_ID values must be UUIDs');
      }
      return id;
    });
    return { segmentId, kind: 'VALUE_ID', values, ...scope };
  }
  if (record.kind === 'TEXT') {
    const text = typeof record.value === 'string' ? record.value.trim() : '';
    if (!text) {
      throw new Error('classificationSegmentFilters TEXT requires a non-empty value');
    }
    if (text.length > CLASSIFICATION_SEGMENT_FILTER_LIMITS.maxTextLength) {
      throw new Error('classificationSegmentFilters TEXT exceeds the maximum length');
    }
    return { segmentId, kind: 'TEXT', value: text, ...scope };
  }
  throw new Error('classificationSegmentFilters.kind must be VALUE_ID or TEXT');
}

/** Digits-only comparison key when the query has no letters (formatted or raw CNPJ). */
export function classificationSegmentNumericQuery(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || /[a-zA-Z]/.test(trimmed)) return null;
  if (!/^[\d.\-/\s]+$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length > 0 ? digits : null;
}

/** Write-side split accepted before persistence assigns identifiers and derived amounts. */
export interface DimensionSplitInput {
  splitId?: string;
  id: string;
  amount?: number;
  percent: number;
}

export interface SegmentSplitInput {
  splitId?: string;
  segmentId: string;
  segmentValueId?: string;
  textValue?: string;
  amount?: number;
  percent: number;
}

export interface CustomBreakdownSplitInput {
  splitId?: string;
  label?: string;
  percent: number;
  amount?: number;
  dimensions: {
    glAccounts: DimensionSplitInput[];
    costCenters?: DimensionSplitInput[];
    departments?: DimensionSplitInput[];
    managerialAccounts?: DimensionSplitInput[];
    segments?: SegmentSplitInput[];
  };
}

export type CustomBreakdownInput = CustomBreakdownSplitInput[];

export interface CustomBreakdownSplit {
  splitId?: string;
  label?: string;
  percent: number;
  amount: number;
  dimensions: {
    glAccounts: DimensionSplit[];
    costCenters?: DimensionSplit[];
    departments?: DimensionSplit[];
    managerialAccounts?: DimensionSplit[];
    segments?: SegmentSplit[];
  };
}

export type CustomBreakdown = CustomBreakdownSplit[];
